import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotLottleLoader } from '../../../components/Loader';
import axiosInstant from '../../../api';
import { useUser } from '../../../hooks/useUser';
import { getBrandProducts } from '../../../api/endpoints';
import { ISingleProduct } from '../../admin/productsV2/productListView/constants';
import { SingleProduct } from '../../admin/productsV2/productListView/SingleProduct';
import { ProductV2Modal } from '../../admin/productsV2/productModal';
import { ReactComponent as NoProductsIcon } from '../../../assets/svg/no-products.svg';
import { ReactComponent as AddIcon } from '../../../assets/svg/add-icon.svg';
import { usePortalPath } from '../../../hooks/usePortalPath'
import { useWorkspaceBrandId } from '../../../hooks/useWorkspaceBrandId';

interface ProductsProps {
  limit?: number;
  onCount?: (count: number) => void;
  // Called after a product is added from this card's modal.
  onProductAdded?: () => void;
}

const Products: React.FC<ProductsProps> = ({
  limit = 50,
  onCount,
  onProductAdded,
}) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const workspaceBrandId = useWorkspaceBrandId();
  const { brand } = useUser();
  const [products, setProducts] = useState<ISingleProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const brandId = workspaceBrandId || (brand ? brand[0].id : 0);
      const response = await axiosInstant.get(
        getBrandProducts(brandId, limit, 0, ''),
      );

      const { data } = response;
      if (data.rows && data.rows.length > 0) {
        setProducts(data.rows);
        setTotalCount(data.count);
        onCount?.(data.count);
      } else {
        setProducts([]);
        setTotalCount(0);
        onCount?.(0);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (brand || workspaceBrandId) {
      fetchProducts();
    }
  }, [limit, brand, workspaceBrandId]);

  useEffect(() => {
    const handleOpenAddProductModal = () => {
      setShowAddProductModal(true);
    };

    window.addEventListener('openAddProductModal', handleOpenAddProductModal);

    return () => {
      window.removeEventListener(
        'openAddProductModal',
        handleOpenAddProductModal,
      );
    };
  }, []);

  const handleViewAll = () => {
    navigate(p('/products?limit=20'));
  };

  const handleProductClick = (productId: number) => {
    if (productId) {
      navigate(p(`/products?productId=${productId}&limit=20`));
    }
  };

  const handleAddProduct = () => {
    setShowAddProductModal(true);
  };

  const handleProductAdded = () => {
    setShowAddProductModal(false);
    fetchProducts();
    onProductAdded?.();
  };

  const handleCloseModal = () => {
    setShowAddProductModal(false);
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full min-h-0">
        <DotLottleLoader fill={false} />
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <>
        <Box className="flex flex-col h-full min-h-0">
          <Box className="flex items-center justify-between flex-shrink-0">
            <Typography
              sx={{
                color: 'var(--text)',
                fontFamily: 'CircularStd',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Products
            </Typography>
          </Box>

          <Box className="flex flex-col items-center justify-center flex-1 w-full gap-[21px]">
            <NoProductsIcon />
            <Typography
              sx={{
                color: 'var(--Base-600, #727272)',
                fontFamily: '"CircularStd"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 450,
                lineHeight: 'normal',
                textAlign: 'center',
                width: '100%',
              }}
            >
              You haven't added any products
            </Typography>
            <Button
              onClick={handleAddProduct}
              sx={{
                backgroundColor: 'var(--accent)',
                color: 'var(--Base-0, #FFF)',
                fontFamily: 'CircularStd',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 900,
                lineHeight: 'normal',
                textTransform: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                alignSelf: 'center',
                '&:hover': {
                  backgroundColor: '#8A6D41',
                },
              }}
            >
              <AddIcon />
              Add Product
            </Button>
          </Box>
        </Box>

        {showAddProductModal && (
          <ProductV2Modal
            product={undefined}
            onClose={handleCloseModal}
            onComplete={handleProductAdded}
          />
        )}
      </>
    );
  }

  return (
    <Box className="flex flex-col h-full min-h-0">
      <Box className="flex items-center justify-between mb-4 flex-shrink-0 max-md:flex-wrap max-md:gap-2">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          Products ({totalCount})
        </Typography>
        <Box className="flex items-center gap-4 max-md:gap-2">
          <Button
            onClick={handleViewAll}
            sx={{
              color: 'var(--accent-text)',
              fontFamily: 'CircularStd',
              fontSize: { xs: '12px', md: '14px' },
              fontWeight: 450,
              textTransform: 'none',
              padding: 0,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            View All
          </Button>
          <Button
            onClick={handleAddProduct}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: { xs: '11px', md: '14px' },
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: 'normal',
              textTransform: 'none',
              borderRadius: '12px',
              padding: { xs: '6px 12px', md: '8px 16px' },
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            + Add Product
          </Button>
        </Box>
      </Box>

      <Box
        className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
        sx={{
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        }}
      >
        <Box
          className="flex gap-4 pb-2 h-full"
          style={{ width: 'max-content' }}
        >
          {products.map((product) => (
            <div
              key={product.desireid}
              className="flex-shrink-0 max-md:!w-[230px]"
              style={{ width: '296px' }}
            >
              <div onClick={() => handleProductClick(product.desireid)}>
                <SingleProduct product={product} onUpdate={() => {}} />
              </div>
            </div>
          ))}
        </Box>
      </Box>

      {showAddProductModal && (
        <ProductV2Modal
          product={undefined}
          onClose={handleCloseModal}
          onComplete={handleProductAdded}
        />
      )}
    </Box>
  );
};

export default Products;
